import { PdfLayout } from '../PdfLayout';
import { HardDrive, DatabaseZap, ShieldCheck, ServerCog, Table2, Send } from 'lucide-react';

const benefits = [
  {
    icon: HardDrive,
    title: 'Store & Forward',
    text: 'When the historian database is unavailable, tag changes are buffered on the appliance and replayed in order, with their original timestamps, at a pace you set.',
  },
  {
    icon: ShieldCheck,
    title: 'Quarantine, Not Silent Loss',
    text: 'Points the database rejects are set aside for review, to retry or discard. Status tags show buffering and forwarding, ready to alarm on.',
  },
  {
    icon: DatabaseZap,
    title: 'Remote Databases',
    text: 'Connect to SQL Server and SQL Express, MySQL/MariaDB, PostgreSQL, Oracle and ODBC sources. Use their tables in dashboards, forms, scripts and named queries.',
  },
  {
    icon: ServerCog,
    title: 'SQL Historian',
    text: 'Write tag history to your own SQL database instead of, or as well as, the built-in historian — and run both while you verify the switch.',
  },
  {
    icon: Table2,
    title: 'Transaction Groups',
    text: 'Snapshot several tags into one row of a local or remote SQL table on a timer, tag change, rising edge, condition or schedule, then write a handshake tag back so the PLC knows it was stored.',
  },
  {
    icon: Send,
    title: 'Outbound REST',
    text: 'Push JSON built from live tag values to an ERP, cloud service or webhook on a timer, on a tag change or on demand. Credentials are stored encrypted.',
  },
];

export function DataInfrastructure() {
  return (
    <PdfLayout
      category="Infrastructure"
      headline="Keep Data Moving with"
      headlineHighlight="Data Infrastructure"
      description="Get plant data where it needs to go and keep it safe on the way. Buffer history through database outages, read and write the SQL databases you already run, log snapshots to tables and push data to other systems — all from one page on the appliance."
      accentColor="#06b6d4"
      benefits={benefits}
    >
      <InfraMockup />
    </PdfLayout>
  );
}

function InfraMockup() {
  const accent = '#06b6d4';
  return (
    <div className="glass-panel p-3 w-full max-w-[280px]">
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-status-fault/60" />
          <div className="w-2 h-2 rounded-full bg-status-idle/60" />
          <div className="w-2 h-2 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[9px] text-text-muted">Data Infrastructure</span>
      </div>

      {/* Tabs */}
      <div className="glass-card p-1.5 mb-2 flex items-center gap-1 flex-wrap justify-center">
        {['Historian', 'InfluxDB S&F', 'Remote DB S&F', 'Transaction Groups', 'Outbound API'].map((t, i) => (
          <span
            key={t}
            className="text-[6px] px-1.5 py-0.5 rounded-md font-medium"
            style={i === 1 ? { backgroundColor: `${accent}1a`, color: accent } : { color: '#94a3b8' }}
          >
            {t}
          </span>
        ))}
      </div>

      {/* Buffer status */}
      <div className="glass-card p-2 mb-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[7px] text-text-muted">InfluxDB Buffer Status</span>
          <span className="text-[7px] font-medium" style={{ color: accent }}>Forwarding…</span>
        </div>
        <div className="grid grid-cols-4 gap-1">
          {[
            { label: 'Pending', value: '1,842', color: '#ca8a04' },
            { label: 'Sent', value: '48,310', color: '#16a34a' },
            { label: 'Failed', value: '3', color: '#dc2626' },
            { label: 'Total', value: '50,155', color: '#334155' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-[9px] font-bold" style={{ color: s.color }}>{s.value}</div>
              <div className="text-[6px] text-text-muted">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="mt-1.5 h-1.5 bg-glass-border rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: '96%', backgroundColor: accent }} />
        </div>
        <div className="text-[6px] text-text-muted mt-1">Replaying every 15 s · oldest pending 06:42:10</div>
      </div>

      {/* Remote connections */}
      <div className="glass-card p-2 mb-2">
        <span className="text-[7px] text-text-muted block mb-1">Remote Database Connections</span>
        {[
          { name: 'ERP', type: 'MSSQL / SQL Express', mode: 'Read only' },
          { name: 'Quality', type: 'PostgreSQL', mode: 'Read/Write' },
          { name: 'MES', type: 'Oracle', mode: 'Read only' },
        ].map((c) => (
          <div key={c.name} className="flex items-center gap-2 text-[7px] py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-status-running shrink-0" />
            <span className="text-text-primary font-medium w-10">{c.name}</span>
            <span className="text-text-muted flex-1">{c.type}</span>
            <span className="text-text-muted">{c.mode}</span>
          </div>
        ))}
      </div>

      {/* Transaction group */}
      <div className="glass-card p-2">
        <div className="flex items-center justify-between">
          <span className="text-[7px] text-text-primary font-medium">Hourly press snapshot</span>
          <span className="text-[6px] text-text-muted">Timer · 3600 s</span>
        </div>
        <div className="text-[6px] text-text-muted mt-0.5">→ Hourly Press Log · die_temp, part_count, job</div>
      </div>

      <div className="flex items-center justify-between mt-2 px-1">
        <span className="text-[8px] text-text-muted">Historian: InfluxDB + SQL</span>
        <span className="text-[8px] text-status-running font-medium">● Backends reachable</span>
      </div>
    </div>
  );
}
